import { SubscriptionTier } from '@apps/www/src/__generated__/graphql';
import SVTrackedUpgradeButtonContainer from '@apps/www/src/www/containers/SVTrackedUpgradeButtonContainer';
import useAuthActiveSubscriptionTiers from '@apps/www/src/www/hooks/useAuthActiveSubscriptionTiers';
import SVModal from '@pkgs/shared-client/components/SVModal';
import NeedsUpgradeReason from '@pkgs/shared/enums/NeedsUpgradeReason';
import UpgradeButtonSource from '@pkgs/shared/enums/UpgradeButtonSource';
import { getSubscriptionTierLabel, getUpsellBenefits } from '@pkgs/shared/plans';
import SVA from '../../../../../packages/shared-client/components/SVA';
import SVButton, { SVButtonUSES } from '../../../../../packages/shared-client/components/SVButton';
import SVLink from '../../../../../packages/shared-client/components/SVLink';
import SVUpgradeFeaturesList from '../../../../../packages/shared-client/components/SVUpgradeFeaturesList';

const getCurrentPlan = (activeTiers: SubscriptionTier[]) => {
	const currentTier = activeTiers[0];
	return currentTier ? getSubscriptionTierLabel(currentTier) : 'Free';
};

export const ModalContent = ({
	title,
	body,
	button,
}: {
	title: React.ReactNode;
	body: React.ReactNode;
	button: React.ReactNode;
}) => (
	<div className="flex flex-col space-y-10">
		<div className="flex flex-col space-y-4">
			<div className="type-small-title block max-w-[300px] text-left font-medium">
				{title}
			</div>
			<div>
				<p className="text-xs text-gray-400">{body}</p>
			</div>
		</div>
		{button}
	</div>
);

const getModalContent = (
	reason: ValueOf<typeof NeedsUpgradeReason>,
	activeTiers: SubscriptionTier[],
) => {
	let title: React.ReactNode = null;
	let body: React.ReactNode = null;
	let button: React.ReactNode = null;
	const upsellBenefits = getUpsellBenefits();
	const currentPlan = getCurrentPlan(activeTiers);
	const isFree = currentPlan === 'Free';

	switch (reason) {
		case NeedsUpgradeReason.RENEW:
			title = (
				<>
					Your subscription
					<br />
					has&nbsp;ended
				</>
			);
			body = (
				<>
					We need your attention, please{' '}
					<SVA Component={SVLink} className="text-brand" to="/billing/">
						update your billing info
					</SVA>{' '}
					to avoid losing access to your saves.
				</>
			);
			button = (
				<SVButton
					Component={SVLink}
					className="w-fit"
					use={SVButtonUSES.PRIMARY}
					to="/upgrade/"
				>
					Restart subscription
				</SVButton>
			);
			break;
		case NeedsUpgradeReason.MAX_FREE_ITEMS_REACHED:
		case NeedsUpgradeReason.MAX_FREE_BOARDS_REACHED:
		default:
			title = (
				<div className="w-full min-w-[340px]">
					<>
						You've hit the limit <br /> on your {currentPlan} plan
					</>
				</div>
			);
			body = (
				<div className="flex max-w-[280px] flex-col">
					{isFree ? (
						<>
							Upgrade to unlock unlimited inspiration and features designed for
							creatives like you.
						</>
					) : (
						<>
							Upgrade to unlock more features designed <br />
							for creatives like you.
						</>
					)}
					<div className="mt-8">
						<SVUpgradeFeaturesList
							features={upsellBenefits}
							checkIconClassName="bg-[#0015FF]"
							className="space-y-2 text-gray-400"
							featureLabelClassName="text-xs"
						/>
					</div>
				</div>
			);
			button = (
				<SVTrackedUpgradeButtonContainer
					source={UpgradeButtonSource.REACHED_LIMIT_MODAL}
					className="w-fit"
					to={`/upgrade/?reason=${
						reason === NeedsUpgradeReason.MAX_FREE_BOARDS_REACHED
							? 'board-limit'
							: 'image-limit'
					}`}
				>
					Upgrade and continue
				</SVTrackedUpgradeButtonContainer>
			);
			break;
	}

	return { title, body, button };
};

const SVUpgradeWarningModal = SVModal.create(
	({ reason }: { reason: ValueOf<typeof NeedsUpgradeReason> }) => {
		const activeTiers = useAuthActiveSubscriptionTiers();
		const { title, body, button } = getModalContent(reason, activeTiers);

		return <ModalContent title={title} body={body} button={button} />;
	},
	{
		modalClassName: 'max-w-[430px] -sm:max-w-full',
		use: SVModal.USES.SMALL,
	},
);

export default SVUpgradeWarningModal;
